import { Box, BoxProps, IconButton, MenuItem, Popover, Typography, useTheme } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AddressActions } from '../../store/Address'
import { AmericanStatesActions, AmericanStatesSelector } from '../../store/AmericanStates'
import { CountyActions } from '../../store/County'
import { MapActions } from '../../store/Map'
import {
  WaterSystemCoordinatesActions,
  WaterSystemsMetricsActions,
} from '../../store/publicWaterSystem'
import {
  WaterSystemsActions,
  WaterSystemsSelector,
  WaterSystemType,
} from '../../store/waterSystems'
import { WellWaterSystemsMetricsActions } from '../../store/wellWaterSystem'
import { DropdownIcon } from '../icons/DropdownIcon'

interface StatesDropDownProps {
  ContainerProps?: BoxProps
}

export const StatesDropdown: FC<StatesDropDownProps> = ({ ContainerProps }) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const states = useSelector(AmericanStatesSelector.makeSortedStates)
  const selectedState = useSelector(AmericanStatesSelector.getSelectedState)
  const fetchingStates = useSelector(AmericanStatesSelector.fetchingStates)
  const waterSystemType = useSelector(WaterSystemsSelector.selectSelectedWaterSystemType)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    dispatch(AmericanStatesActions.fetchAmericanStatesAsync() as any)
  }, [dispatch])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleStateChange = (stateId: string) => {
    const selectedState = states.find((state) => state._id === stateId)

    if (!selectedState) return

    resetSearchedData()



    if (selectedState.location) {

      const [lat, lng] = selectedState.location
      const zoom = selectedState.default_zoom

      dispatch(AddressActions.setSelectedAddress(null))
      dispatch(MapActions.setMapCenter({ lat, lng }))
      dispatch(MapActions.setMapZoom({ zoom }))
      dispatch(CountyActions.setSelectedCounty(''))
      dispatch(AmericanStatesActions.setSelectedState(selectedState))

    }

    const options = {
      params: {
        state_abbr: selectedState?.state_abbr,
      },
    }

    if (waterSystemType === WaterSystemType.WELL) {
      dispatch(WellWaterSystemsMetricsActions.fetchWellWaterSystemMetricsAsync({ options }) as any)
    } else {
      dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({ options }) as any)
    }
    setAnchorEl(null)
  }

  const resetSearchedData = () => {
    dispatch(WaterSystemsActions.setSelectedWaterSystem(''))
    dispatch(WaterSystemsActions.resetWaterSystemsData())
    dispatch(WaterSystemsActions.setUserSearched(false))
    dispatch(WaterSystemsMetricsActions.resetWaterSystemMetricsData())
    dispatch(WaterSystemCoordinatesActions.resetWaterSystemCoordinates())
  }

  const open = Boolean(anchorEl)
  const id = open ? 'state-popover' : undefined

  return (
    <Box {...ContainerProps}>
      <IconButton
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#FFF',
          borderRadius: '8px',
          border: '1px solid #D0D5DD',
          padding: '6px 12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#344054',
          '&:hover': {
            backgroundColor: '#F4F5F7',
          },
        }}
      >
        <Typography sx={{ marginRight: '8px' }}>
          {selectedState?.state ? selectedState.state : 'View map by state'}
        </Typography>
        <DropdownIcon />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '8px',
            boxShadow: theme.shadows[1],
            padding: '8px 0',
            width: '176px',
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: '#FFF',
          },
        }}
      >
        {fetchingStates ? (
          <MenuItem disabled>Loading states...</MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() => handleStateChange('')}
              sx={{
                fontSize: '14px',
                color: theme.palette.secondary.dark,
                padding: '8px 16px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              View map by state
            </MenuItem>
            {states.map((state) => (
              <MenuItem
                key={state._id}
                onClick={() => handleStateChange(state._id)}
                sx={{
                  fontSize: '14px',
                  color: theme.palette.secondary.dark,
                  padding: '8px 16px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {state.state}
              </MenuItem>
            ))}
          </>
        )}
      </Popover>
    </Box>
  )
}
